'use client'

import { PointsBadge } from '@/features/navbar/components/points-badge/PointsBadge'
import { assets } from '@/ui/assets'

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/shadcn/ui/navigation-menu'
import { GradientBadge } from '@/ui/atoms/badge/Badge'
import { Button } from '@/ui/atoms/button/Button'

import { paths } from '@/config/paths'
import { useAccount } from '@/domain/hooks/useAccount'
import { useUserPreferences } from '@/domain/state'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/shadcn/ui/sheet'
import * as Collapsible from '@/ui/atoms/collapsible/Collapsible'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { useBreakpoint } from '@/ui/utils/useBreakpoint'
import {
  ArrowLeftRight,
  ChevronDown,
  CreditCard,
  DollarSign,
  Grid,
  Menu,
  Shuffle,
  Sparkles,
  Target,
  TrendingUp,
  Trophy,
} from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import { FooterLinks } from '../navbar/components/FooterLinks'
import { WalletButton } from '../navbar/components/wallet-button/WalletButton'

interface NavSubItem {
  id: string
  href: string
  label: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  featureKey?: string // Added to support feature flag filtering on submenus
}

interface NavItem {
  id: string
  href: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  featureKey?: string
  isNew?: boolean
  submenu?: NavSubItem[]
}

function useNavItems(): NavItem[] {
  const account = useAccount()
  const { getFavoriteTab } = useUserPreferences()
  const validDashboardPaths: string[] = [
    paths.dashboardPooled,
    paths.dashboardEarn,
    paths.dashboardLendBorrow,
    paths.dashboardIsolated,
    paths.activity,
  ]
  const storedDashboard = getFavoriteTab('dashboard', account)
  const dashboardHref =
    storedDashboard && validDashboardPaths.includes(storedDashboard) ? storedDashboard : paths.dashboardPooled

  const navItems: NavItem[] = [
    { id: 'quick-borrow', href: paths.borrow, label: 'Borrow', icon: TrendingUp },
    { id: 'earn', href: paths.earn, label: 'Earn', icon: Sparkles },
    { id: 'swap', href: paths.swap, label: 'Swap', icon: ArrowLeftRight },
    { id: 'card', href: paths.card, label: 'Card', icon: CreditCard, featureKey: 'card' },
    { id: 'dashboard', href: dashboardHref, label: 'Dashboard', icon: Grid },
    { id: 'usdxl', href: paths.usdxl, label: 'USDXL', icon: DollarSign },
    {
      id: 'more',
      href: '#more',
      label: 'More',
      icon: ChevronDown,
      submenu: [
        {
          id: 'opportunities',
          href: paths.opportunities,
          label: 'Opportunities',
          icon: Target,
          description: 'Find cross-market arbitrage spreads',
        },
        {
          id: 'bridge',
          href: paths.bridge,
          label: 'Bridge',
          description: 'Bridge assets across chains',
          icon: Shuffle,
        },
        {
          id: 'points',
          href: paths.points,
          label: 'Points',
          description: 'Track your rewards and points',
          icon: Trophy,
        },
      ],
    },
  ]

  return navItems.filter((item) => !item.submenu || item.submenu.length > 0)
}

function isNavItemActive(item: NavItem, pathname: string): boolean {
  if (item.id === 'dashboard') return pathname.startsWith('/dashboard')
  if (item.id === 'earn') return pathname === item.href || pathname.startsWith(`${item.href}/`)
  return pathname === item.href
}

export function TopBar() {
  const account = useAccount()
  const navItems = useNavItems()
  const pathname = usePathname()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const isXl = useBreakpoint('xl')

  useEffect(() => {
    if (isXl) {
      setMobileMenuOpen(false)
    }
  }, [isXl])

  return (
    <div className="relative flex h-12 items-center justify-between border-border border-b bg-card px-2 xl:px-4">
      {/* Left: Logo and Mobile Menu */}
      <div className="flex items-center">
        {/* Mobile Menu */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild className="xl:hidden">
            <Button className="mr-4 h-8 w-8 bg-card p-0 text-secondary-foreground transition-colors hover:bg-muted hover:text-foreground">
              <Menu className="h-4 w-4" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="no-scrollbar flex w-64 flex-col overflow-auto p-0">
            <SheetHeader className="border-border border-b p-4">
              <SheetTitle className="flex items-center">
                <Image
                  src={assets.hypurrLogoHorizontal}
                  alt="HypurrFi"
                  width={160}
                  height={40}
                  className="h-8 w-auto"
                />
              </SheetTitle>
            </SheetHeader>
            <nav className="flex flex-1 flex-col gap-1 p-3">
              {navItems.map((item) => {
                const Icon = item.icon
                if (item.submenu) {
                  return (
                    <Collapsible.Root key={item.id} className="group w-full" defaultOpen>
                      <div className="flex w-full items-center justify-between gap-3 rounded-xs px-4 py-3 font-medium text-base text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                        <Link
                          href={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex flex-1 items-center gap-3"
                        >
                          <Icon className="h-5 w-5" />
                          <span>{item.label}</span>
                        </Link>
                        <Collapsible.Trigger asChild>
                          <button type="button" className="p-1 hover:text-foreground">
                            <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                          </button>
                        </Collapsible.Trigger>
                      </div>
                      <Collapsible.Content className="ml-8 space-y-1 data-[state=open]:pt-1">
                        {item.submenu.map((subItem) => {
                          const SubIcon = subItem.icon
                          return (
                            <Link
                              key={subItem.href}
                              href={subItem.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className={cn(
                                'flex items-center gap-2 rounded-xs px-3 py-2 text-sm transition-colors',
                                pathname === subItem.href
                                  ? 'bg-muted text-foreground'
                                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                              )}
                            >
                              <SubIcon className="h-4 w-4" />
                              <span>{subItem.label}</span>
                            </Link>
                          )
                        })}
                      </Collapsible.Content>
                    </Collapsible.Root>
                  )
                }
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      'flex items-center gap-3 rounded-xs px-4 py-3 font-medium text-base transition-colors',
                      isNavItemActive(item, pathname)
                        ? 'bg-muted text-foreground'
                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                    {item.isNew && (
                      <span className="block">
                        <GradientBadge innerClassName="py-0">
                          <Typography variant="prompt" gradient className="text-[10px]">
                            New
                          </Typography>
                        </GradientBadge>
                      </span>
                    )}
                  </Link>
                )
              })}
            </nav>

            <div className="mt-auto border-border border-t p-3">
              <FooterLinks mobileMenuCollapsed={false} />
            </div>
          </SheetContent>
        </Sheet>

        {/* Logo */}
        <Link href="/" className="flex items-center">
          <Image src={assets.hypurrLogoHorizontal} alt="HypurrFi" width={160} height={40} className="h-8 w-auto" />
        </Link>
      </div>

      {/* Center: Desktop Navigation */}
      <NavigationMenu className="-translate-x-1/2 absolute left-1/2 z-50 hidden transform xl:flex">
        <NavigationMenuList>
          {navItems.map((item) => (
            <NavigationMenuItem key={item.href}>
              {item.submenu ? (
                <>
                  <NavigationMenuTrigger
                    className={cn(
                      'h-9 bg-transparent px-3 text-secondary-foreground text-sm',
                      pathname.startsWith(item.href) && 'bg-foreground/4 text-foreground',
                    )}
                  >
                    <Link href={item.href} className="hover:text-foreground">
                      {item.label}
                    </Link>
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[320px] p-3">
                      <ul className="grid gap-2">
                        {item.submenu.map((subItem) => {
                          const Icon = subItem.icon
                          return (
                            <li key={subItem.href}>
                              <NavigationMenuLink asChild>
                                <Link
                                  href={subItem.href}
                                  className={cn(
                                    'block select-none space-y-1 rounded-md p-3 text-secondary-foreground leading-none no-underline outline-none transition-colors focus:bg-secondary hover:bg-secondary focus:text-accent-foreground hover:text-accent-foreground',
                                    pathname === subItem.href && 'bg-muted text-foreground',
                                  )}
                                >
                                  <div className="flex items-center gap-2">
                                    <Icon className="h-4 w-4" />
                                    <div className={'font-medium text-sm leading-none'}>{subItem.label}</div>
                                  </div>
                                  <p className="line-clamp-2 text-muted-foreground text-sm leading-snug">
                                    {subItem.description}
                                  </p>
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          )
                        })}
                      </ul>
                      {item.id === 'more' && (
                        <div className="mt-3 border-border border-t pt-3">
                          <FooterLinks mobileMenuCollapsed={false} />
                        </div>
                      )}
                    </div>
                  </NavigationMenuContent>
                </>
              ) : (
                <Link
                  href={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    'inline-flex h-9 w-max items-center justify-center gap-2 rounded-xs px-3 py-2 font-medium text-secondary-foreground text-sm transition-colors disabled:pointer-events-none focus:bg-accent hover:bg-accent focus:text-accent-foreground hover:text-accent-foreground disabled:opacity-50 focus:outline-none',
                    isNavItemActive(item, pathname) && 'bg-foreground/4 text-foreground',
                  )}
                >
                  <span>{item.label}</span>
                  {item.isNew && (
                    <GradientBadge innerClassName="py-0">
                      <Typography variant="prompt" gradient className="text-[10px]">
                        New
                      </Typography>
                    </GradientBadge>
                  )}
                </Link>
              )}
            </NavigationMenuItem>
          ))}
        </NavigationMenuList>
      </NavigationMenu>

      <div className="flex items-center gap-4 ">
        {/* Points Badge - only show when connected */}
        {account && <PointsBadge className="hidden h-9 py-0 xl:flex" />}

        <WalletButton />
      </div>
    </div>
  )
}
